import { Component } from '@angular/core';

// RxJS
import { Subject, takeUntil } from 'rxjs';

// Lodash
import { cloneDeep } from 'lodash-es';

// Services
import { ProjectService } from '@modules/project/services/project.service';
import { UsersService } from '@modules/users/services/users.service';
import { AlertService } from '@modules/elements/services/alert.service';

// Components
import { CreateDestinationComponent } from '../create-destination/create-destination.component';

// Pipes
import { DifferencePipe } from '@modules/elements/pipes/difference.pipe';

// Types
import { Project } from '@modules/project/types/project';
import { RecordOptions } from '@modules/records/types/record-options';
import { UserPermissions } from '@modules/users/types/user';
import { ServerTemplate } from '@modules/project/types/server-template';
import { MatDialog } from '@angular/material/dialog';
import { StationsListComponent } from '../stations-list/stations-list.component';

type SlackNotificationField =
  | 'slackNotificationStart'
  | 'slackNotificationFinish'
  | 'slackNotificationError'
  | 'slackNotificationRecordCreated'
  | 'slackNotificationServerStarted';

@Component({
    selector: 'app-project-details',
    templateUrl: './project-details.component.html',
    styleUrl: './project-details.component.less',
    standalone: false
})
export class ProjectDetailsComponent {

  // Public
  public currentProject?: Project;
  public originProject?: Project;
  public projects: Project[] = []
  public recordOptions = new RecordOptions();
  public editing = false;
  public permissions?: UserPermissions[];
  public serverTemplates: ServerTemplate[] = [];
  public slackNotificationFields: { key: SlackNotificationField; title: string }[] = [
    { key: 'slackNotificationRecordCreated', title: 'Record Created' },
    { key: 'slackNotificationServerStarted', title: 'Server Started' },
    { key: 'slackNotificationStart',  title: 'Record Start' },
    { key: 'slackNotificationFinish', title: 'Record Finish' },
    { key: 'slackNotificationError',  title: 'Error' },
  ];
  public destinations: {
    id: string,
    name?: string,
    bucket?: string,
    profile?: string,
    acl?: string
  }[] = []

  // Private
  private alive = new Subject<void>();

  /**
   * Constructor
   */

  constructor(
    private projectService: ProjectService,
    private differencePipe: DifferencePipe,
    private usersService: UsersService,
    private alertService: AlertService,
    private dialog: MatDialog,
  ) {
  }

  /**
   * Component lifecycle
   */

  ngOnInit(): void {
    // Get Project
    this.projectService.getCurrentProject()
      .pipe(takeUntil(this.alive))
      .subscribe(project => {
        this.originProject = cloneDeep(project);
        this.currentProject = project;
        if (this.currentProject?.destinations) {
          this.destinations = Object.entries(this.currentProject.destinations).map(([id, data]) => ({id, ...data}));
        }
        this.updateProject();
        this.getServerTemplates();
      });
    // Get Projects
    this.projectService.getProjects()
      .pipe(takeUntil(this.alive))
      .subscribe(projects => this.projects = projects);
    // Get Permissions
    this.usersService.getPermissions()
      .pipe(takeUntil(this.alive))
      .subscribe(permissions => this.permissions = permissions);
  }

  ngOnDestroy(): void {
    this.alive.next();
    this.alive.complete();
  }

  /**
   * Methods
   */

  getServerTemplates(): void {
    if (this.currentProject?.id) {
      this.projectService.getServerTemplates(this.currentProject?.id)
        .pipe(takeUntil(this.alive))
        .subscribe(serverTemplates => this.serverTemplates = serverTemplates);
    } else {
      this.serverTemplates = [];
    }
  }

  updateStartTrigger(option?: any): void {
    if (option && this.currentProject?.startTrigger) {
      this.currentProject.startTrigger.type = option.value;
      if (option.value === 'manual' || option.value === 'scteTrigger') {
        this.currentProject.startTrigger.value = option.value;
      }
      this.updateProject();
    }
  }

  updateDestination(): void {
    if (this.currentProject?.destinations) {
      this.currentProject.destinations = this.destinations.reduce((acc, { id, ...rest }) => {
        acc[id] = rest;
        return acc;
      }, {} as any);
      this.updateProject();
    }
  }

  addDestination(): void {
    // TODO Add logic for custom Destionations
    this.dialog.open(CreateDestinationComponent, {data: {}, disableClose: true})
      .afterClosed()
      .subscribe(result => {
        if (result.id) {
          this.destinations.push(result);
          this.updateDestination();
        }
      });
  }

  deleteDestination(key: string): void {
    // TODO Add logic for custom Destionations
    const index = this.destinations.findIndex(item => item.id === key);
    if (index !== -1) {
      this.destinations.splice(index, 1);
      this.updateDestination();
    }
  }

  updateDestinationFlags(option: any, id: string): void {
    const index = this.destinations.findIndex(item => item.id === id);
    if (option && this.destinations && index !== -1) {
      this.destinations[index].acl = option.value;
      this.updateDestination();
    }
  }

  uploadImage(event: any): void {
    const file = event.target?.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (this.currentProject) {
          this.currentProject.photoUrl = reader.result as string;
          this.projectService.updateProject(this.currentProject);
        }
      }
      reader.readAsDataURL(file);
    }
  }

  updateProject(): void {
    if (this.currentProject) {
      const projectDiff: Partial<Project> = this.differencePipe.transform(this.currentProject, this.originProject);
      this.editing = Object.keys(projectDiff).length > 0;
    }
  }

  addSlackUrl(field: SlackNotificationField): void {
    if (!this.currentProject) return;
    if (!this.currentProject.settings) this.currentProject.settings = {};
    (this.currentProject.settings[field] ??= []).push('');
    this.updateProject();
  }

  removeSlackUrl(field: SlackNotificationField, index: number): void {
    const urls = this.currentProject?.settings?.[field];
    if (urls && index > -1) {
      urls.splice(index, 1);
      this.updateProject();
    }
  }

  isSlackWebhookUrl(url: string): boolean {
    return /^https:\/\/hooks\.slack\.com\/.+/.test((url ?? '').trim());
  }

  trimSlackUrl(field: SlackNotificationField, index: number): void {
    const urls = this.currentProject?.settings?.[field];
    if (urls && urls[index] !== undefined) urls[index] = urls[index].trim();
    this.updateProject();
  }

  private cleanupSlackUrls(): void {
    const settings = this.currentProject?.settings;
    if (!settings) return;
    for (const { key } of this.slackNotificationFields) {
      const urls = settings[key];
      if (urls) {
        settings[key] = urls.map(u => u.trim()).filter(u => u.length > 0);
        if (!settings[key]!.length) delete settings[key];
      }
    }
  }

  /**
   * Actions
   */

  saveProject(): void {
    if (this.currentProject) {
      this.cleanupSlackUrls();
      this.projectService.updateProject(this.currentProject);
    }
  }

  resetProjectChanges(): void {
    if (this.originProject) {
      this.projectService.updateProject(this.originProject);
    }
  }

  addServer(): void {
    if (this.currentProject?.id) {
      const server: ServerTemplate = ServerTemplate.createDefault();
      this.projectService.createServerTemplates(this.currentProject?.id, server)
        .pipe(takeUntil(this.alive))
        .subscribe(() => this.getServerTemplates());
    }
  }

  openC3Stations(): void {
    this.dialog.open(StationsListComponent, {data: {}, disableClose: true});
  }

  deleteProject(): void {
    if (this.currentProject?.id) {
      this.alertService.show(
        'Detele Project',
        `Would you like to delete '${this.currentProject.name}' project?`,
        ['Yes', 'No'],
        0
      )
      .subscribe(response => {
        if (response === 0) {
          this.projectService.deleteProject(this.currentProject!.id!);
        }
      });
    }
  }

}
